import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Asset {
  id: string
  name: string
  category: string
  location: string
  status: string
  value: number
  purchase_date: string
  depreciation: number
}

interface AssetCategory {
  category: string
  count: number
  total_value: number
  avg_age_years: number
  maintenance_due: number
}

interface Statistics {
  total_assets: number
  total_value: number
  total_depreciation: number
  assets_active: number
  assets_maintenance: number
  maintenance_due: number
}

export default function AssetTrackingPage() {
  const [assets, setAssets] = useState<Asset[]>([])
  const [categories, setCategories] = useState<AssetCategory[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [assetsRes, categoriesRes, statsRes] = await Promise.all([
        fetch('/api/assettracking/assets'),
        fetch('/api/assettracking/categories'),
        fetch('/api/assettracking/statistics')
      ])
      if (assetsRes.ok) setAssets(await assetsRes.json())
      if (categoriesRes.ok) setCategories(await categoriesRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching asset tracking data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800'
    if (status === 'maintenance') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Asset Tracking</h1>
          <p className="mt-2 text-gray-600">Phase 255 - Physical and digital asset management</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Assets</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_assets)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_value)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Depreciation</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(statistics.total_depreciation)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.assets_active}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">In Maintenance</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.assets_maintenance}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Maintenance Due</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.maintenance_due}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Asset Inventory</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Depreciation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assets.map((asset) => (
                  <tr key={asset.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{asset.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{asset.category}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{asset.location}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(asset.status)}`}>{asset.status}</span>
                    </td>
                    <td className="px-6 py-4 text-green-600 font-medium">{formatCurrency(asset.value)}</td>
                    <td className="px-6 py-4 text-red-600">{formatCurrency(asset.depreciation)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Assets by Category</h2>
          </div>
          <div className="p-6 space-y-4">
            {categories.map((cat, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{cat.category}</span>
                  <span className="text-lg font-bold text-green-600">{formatCurrency(cat.total_value)}</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Count</p>
                    <p className="font-medium text-blue-600">{cat.count}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Age</p>
                    <p className="font-medium text-purple-600">{cat.avg_age_years}y</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Maint Due</p>
                    <p className="font-medium text-orange-600">{cat.maintenance_due}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Avg Value</p>
                    <p className="font-medium text-green-600">{formatCurrency(cat.total_value / cat.count)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
